/**
 * Wald-Kopf der Preisautomatik-Übersicht (Board „Preisautomatik · Übersicht“): Titel, Zeile mit
 * Apartments und Automatik, Chips letzter Lauf (spätester je Apartment) · nächster Lauf · zuletzt
 * bestätigt und rechts die Karte „Ganzer Workspace“ mit Zustand und Stoppen/Freigeben.
 */
import { PageHeader } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { WORKSPACE_TEXT, type WorkspaceState } from './overviewModel';

interface Props {
  subline: string;
  chips: { big: string; text: string }[];
  state: WorkspaceState | null;
  /** Die Mandanten-Steuerung selbst – auch bei globalem Stopp kann sie gestoppt sein. */
  tenantKilled: boolean;
  globalEnabled: boolean;
  /** Nur mit Verwaltungsrecht und vorhandener Steuerung. */
  onToggle?: () => void;
}

export function OverviewHeader({
  subline,
  chips,
  state,
  tenantKilled,
  globalEnabled,
  onToggle,
}: Props) {
  const stopped = state !== 'running';
  const tenant =
    state === 'missing'
      ? 'Workspace ohne Steuerung'
      : `Workspace selbst ${tenantKilled ? 'gestoppt' : 'freigegeben'}`;
  return (
    <PageHeader
      className="pb-[96px] md:pb-[96px]"
      title="Preisautomatik"
      description={
        <span className="flex flex-col gap-2.5">
          <span className="text-on-forest-soft text-sm">{subline}</span>
          {chips.length > 0 && (
            <span className="flex flex-wrap gap-2">
              {chips.map((chip) => (
                <span
                  key={chip.text}
                  className="rounded-pill flex h-9 items-center gap-1.5 bg-white/10 px-3.5 text-[13px] text-white"
                >
                  <b className="font-serif text-base">{chip.big}</b>
                  {chip.text}
                </span>
              ))}
            </span>
          )}
        </span>
      }
      tools={
        state && (
          <div className="rounded-tile flex w-full max-w-[300px] flex-col gap-2 border border-white/15 bg-white/[0.08] p-4 text-white">
            <span className="text-on-forest-soft font-mono text-[10px] font-semibold uppercase tracking-[0.12em]">
              Ganzer Workspace
            </span>
            <span className="flex items-center gap-2.5">
              <span
                aria-hidden
                className={cn(
                  'size-2.5 rounded-full',
                  stopped ? 'bg-sealwax-on-dark' : 'bg-signal',
                )}
              />
              <span className="font-serif text-xl font-bold">{WORKSPACE_TEXT[state]}</span>
            </span>
            <span className="text-on-forest-soft text-xs">
              {globalEnabled ? 'Global freigegeben' : `Global gestoppt · ${tenant}`} · Buchungen und
              manuelle Werte bleiben immer unberührt
            </span>
            {onToggle && (
              <button
                type="button"
                onClick={onToggle}
                className={cn(
                  'rounded-pill h-11 self-start px-4 text-sm font-bold',
                  tenantKilled ? 'text-forest-deep bg-white' : 'bg-sealwax text-white',
                )}
              >
                {tenantKilled ? 'Für ganzen Workspace freigeben' : 'Für ganzen Workspace stoppen'}
              </button>
            )}
          </div>
        )
      }
    />
  );
}
